import { Injectable } from "@angular/core";
import { ValidationResponse } from "./ValidationResponse";

export default class AuthValidation {

    static lowercaseLetters: string = "abcdefghijklmnopqrstuvwxyz";
    static capitalLetters: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    static numbers: string = "0123456789";
    static usernameChars: string = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-._+";
    static emailRegex: string = "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$";

    /**
     * Checks whether a submitted password is valid.
     * @param password The password submitted.
     * @param fieldName The name of the field to be displayed in error messages.
     * @returns Instance of ValidationResponse saying whether the field is valid or not.
     */
    static PasswordValid(password: string, fieldName: string): ValidationResponse {

        if (password.length < 6) {
            return ValidationResponse.Error(fieldName + " should be at least 6 characters.");
        }

        if (password.length > 100) {
            return ValidationResponse.Error(fieldName + " should be at most 100 characters.");
        }

        let hasCapital: boolean = false;
        let hasNumber: boolean = false;
        let hasNonAlphaNumeric: boolean = false;
        for (let i = 0; i < password.length; i++) {
            if (this.capitalLetters.includes(password[i])) {
                hasCapital = true;
                continue;
            }
            if (this.numbers.includes(password[i])) {
                hasNumber = true;
                continue;
            }
            if (!(this.lowercaseLetters.includes(password[i]))) {
                hasNonAlphaNumeric = true;
            }
        }

        if (!hasCapital) {
            return ValidationResponse.Error(fieldName + " requires a capital letter.");
        }

        if (!hasNumber) {
            return ValidationResponse.Error(fieldName + " requires a number.");
        }

        if (!hasNonAlphaNumeric) {
            return ValidationResponse.Error(fieldName + " requires a non-alphanumeric character.");
        }

        return ValidationResponse.Success()
    }

    /**
     * Checks whether a submitted verification key (e.g. email verification key) is valid.
     * @param key The key submitted.
     * @param fieldName The name of the field to be displayed in error messages.
     * @returns Instance of ValidationResponse saying whether the field is valid or not.
     */
    static VerifKeyValid(key: string, fieldName: string): ValidationResponse {

        if (key.length != 6) {
            return ValidationResponse.Error(fieldName + " must be 6 digits.");
        }

        for (let i = 0; i < key.length; i++) {
            if (!this.numbers.includes(key[i])) {
                return ValidationResponse.Error(fieldName + " should consist of digits (0-9).")
            }
        }

        return ValidationResponse.Success();
    }

    /**
     * Checks whether a submitted username is valid.
     * @param user The username submitted.
     * @param fieldName The name of the field to be displayed in error messages.
     * @returns Instance of ValidationResponse saying whether the field is valid or not.
     */
    static UsernameValid(user: string, fieldName: string): ValidationResponse {
        if (user.length < 5) {
            return ValidationResponse.Error(fieldName + " should be at least 5 characters.");
        }
        if (user.length > 50) {
            return ValidationResponse.Error(fieldName + " should be at most 50 characters.");
        }
        for (let i = 0; i < user.length; i++) {
            if (!this.usernameChars.includes(user[i])) {
                return ValidationResponse.Error(fieldName + " can only contain characters a-z, A-Z, 0-9, '-', '.', '_' and '+'.");
            }
        }
        return ValidationResponse.Success();
    }

    /**
     * Checks whether a sumbitted email address is valid.
     * @param email The email addresss submitted.
     * @param fieldName The name of the field to be displayed in error messages.
     * @returns Instance of ValidationResponse saying whether the field is valid or not.
     */
    static EmailAddressValid(email: string, fieldName: string): ValidationResponse {
        if (email.length < 5) {
            return ValidationResponse.Error(fieldName + " should be at least 5 characters.");
        }
        if (email.length > 100) {
            return ValidationResponse.Error(fieldName + " should be at most 100 characters.");
        }
        if (!this.testEmailRegex(email)) {
            return ValidationResponse.Error(fieldName + " not valid.");
        }
        return ValidationResponse.Success();
    }

    /**
     * Checks whether a 'Username or Email' value is valid.
     * @param user The 'Username or Email'.
     * @param fieldName The name of the field to be displayed in error messages.
     * @returns Instance of ValidationResponse saying whether the field is valid or not.
     */
    static UserValid(user: string, fieldName: string): ValidationResponse {
        if (user.includes('@')) {
            // check it is a valid email
            if (user.length < 5) {
                return ValidationResponse.Error("Email should be at least 5 characters.");
            }
            if (user.length > 100) {
                return ValidationResponse.Error("Email should be at most 100 characters.");
            }
            if (!this.testEmailRegex(user)) {
                return ValidationResponse.Error("Not a valid Email Address.");
            }
        } else {
            // check it is a valid username
            if (user.length < 5) {
                return ValidationResponse.Error("Username should be at least 5 characters.");
            }
            if (user.length > 50) {
                return ValidationResponse.Error("Username should be at most 50 characters.");
            }
            for (let i = 0; i < user.length; i++) {
                if (!this.usernameChars.includes(user[i])) {
                    return ValidationResponse.Error("Username can only contain characters a-z, A-Z, 0-9, '-', '.', '_' and '+'.");
                }
            }
        }
        return ValidationResponse.Success();
    }

    static testEmailRegex(email: string): boolean {
        const regexp = new RegExp(this.emailRegex);
        return regexp.test(email);
    }
}